"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  CLUTCH_RATING_LABEL,
  UPWORK_RATING,
  UPWORK_RATING_LABEL,
  UPWORK_STARS_ALT,
} from "@/content/company-stats";
import { UPWORK_BADGE_LABEL } from "@/content/team";
import { Container } from "@/components/ui/Container";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { SanityImage } from "@/components/shared/SanityImage";
import { VerifiedReviewBadge } from "@/components/shared/VerifiedReviewBadge";
import { buildTestimonialFeed } from "@/content/testimonial-feed";
import {
  REVIEW_CLAMP_CHARS,
  REVIEW_CONTEXT,
  TESTIMONIALS_COPY,
  findHighlight,
  splitOnHighlight,
} from "@/content/testimonials-section";
import { CLUTCH_PROFILE_URL, UPWORK_PROFILE_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type {
  ReviewData,
  ReviewSource,
  SiteSettingsData,
  TestimonialsSectionData,
} from "@/sanity/types";
import { NewTabHint } from '@/components/ui/NewTabHint';
import { Eyebrow } from '@/components/ui/Eyebrow';

const PROFILE_URLS: Record<ReviewSource, string> = {
  clutch: CLUTCH_PROFILE_URL,
  upwork: UPWORK_PROFILE_URL,
};

interface TestimonialsProps {
  data: TestimonialsSectionData;
  siteSettings?: SiteSettingsData | null;
  /**
   * Show the "See the portfolio" link in the closing CTA row. Off for focused
   * campaign landings (e.g. /clutch) where off-page links are a distraction.
   */
  showPortfolioLink?: boolean;
  /**
   * Show the Clutch / Upwork rating cards beside the heading. Off on /clutch
   * where the hero already carries the Clutch rating.
   */
  showTrustBadges?: boolean;
  /**
   * Show the "Get a fixed-price quote" button. It points at the homepage
   * contact form, so pages without one (e.g. /clutch, which has its own
   * offer form) turn it off.
   */
  showQuoteCta?: boolean;
  /**
   * True when PageBuilder renders this section inside a shared-background
   * group. The group's gradient is dark in places, so the section content sits
   * on its own light panel there instead of on the bare gradient.
   */
  onDarkBackdrop?: boolean;
  /**
   * Run the CMS reviews through `buildTestimonialFeed` — pins the featured
   * Clutch reviews, drops superseded ones, and interleaves the Upwork reviews.
   * Off for /clutch, which supplies its own curated Clutch-only list and would
   * be undercut by an off-platform review.
   */
  composeFeed?: boolean;
  /**
   * Lead the row with this reviewer instead of the feed's first entry. The
   * feed's first review moves to second place and the rest keep their order.
   * Used on the homepage, whose proof strip already quotes the first review.
   */
  leadReviewerName?: string;
}

/**
 * Move the item named `name` to the front and keep the feed's original first
 * item right behind it (unchanged if absent). The feed opens on its strongest
 * review, so leading with another one must not push that review to the end.
 */
export function rotateToReviewer<T extends { clientName: string }>(
  list: T[],
  name: string | undefined,
): T[] {
  if (!name) return list;
  const at = list.findIndex((r) => r.clientName === name);
  return at <= 0 ? list : [list[at]!, ...list.slice(0, at), ...list.slice(at + 1)];
}

/** Up to two initials from the reviewer's name, for the avatar fallback. */
function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function ArrowIcon({
  direction,
  className,
}: {
  direction: "left" | "right" | "up-right";
  className?: string;
}): React.JSX.Element {
  const d =
    direction === "left"
      ? "M19 12H5m6-6-6 6 6 6"
      : direction === "right"
        ? "M5 12h14m-6-6 6 6-6 6"
        : "M7 17 17 7M9 7h8v8";
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d={d} />
    </svg>
  );
}

function QuoteGlyph({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg viewBox="0 0 32 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M0 24V14.4C0 6.6 4.1 1.8 12 0l1.6 3.3C9 4.6 6.9 7.2 6.6 11H12v13H0Zm18.4 0V14.4c0-7.8 4.1-12.6 12-14.4L32 3.3c-4.6 1.3-6.7 3.9-7 7.7h5.4v13H18.4Z" />
    </svg>
  );
}

interface ReviewCardProps {
  review: ReviewData;
  featured: boolean;
  index: number;
  total: number;
}

function ReviewCard({ review, featured, index, total }: ReviewCardProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Every CMS review predates the `source` field and came from the Clutch
  // profile, so an absent source means Clutch.
  const source: ReviewSource = review.source ?? "clutch";
  const platform = TESTIMONIALS_COPY.platformNames[source];
  const context = REVIEW_CONTEXT[review.clientName];
  const parts = splitOnHighlight(review.review, findHighlight(review.review, context));
  // The featured card has the room to show its quote in full.
  const clampable = !featured && review.review.length > REVIEW_CLAMP_CHARS;
  const clamped = clampable && !expanded;
  const quoteId = `review-quote-${index}`;
  const dark = featured;

  return (
    <article
      aria-label={TESTIMONIALS_COPY.card.label(index + 1, total, review.clientName)}
      className={cn(
        "flex h-full flex-col rounded-[20px] p-6 lg:rounded-[24px] lg:p-8",
        dark
          ? "bg-background text-foreground"
          : "border border-background/[0.07] bg-white text-background",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <VerifiedReviewBadge
          source={source}
          href={review.sourceUrl ?? PROFILE_URLS[source]}
          linkLabel={
            review.sourceUrl
              ? TESTIMONIALS_COPY.card.sourceLinkLabel(platform)
              : source === "upwork"
                ? // The Upwork profile is the founder's own, not an agency page.
                  UPWORK_BADGE_LABEL
                : TESTIMONIALS_COPY.card.clutchProfileLinkLabel
          }
          tone={dark ? "dark" : "light"}
        />
        <QuoteGlyph
          className={cn("h-5 w-auto shrink-0 lg:h-6", dark ? "text-accent" : "text-accent-ink/80")}
        />
      </div>

      <blockquote className="mt-5 lg:mt-6">
        <p
          id={quoteId}
          className={cn(
            "font-light tracking-[-0.01em]",
            featured
              ? "text-[19px] leading-[1.42] lg:text-[24px] lg:leading-[1.38]"
              : "text-[16px] leading-[1.55] lg:text-[17px]",
            clamped && "line-clamp-7",
          )}
        >
          {parts.before}
          {parts.match && (
            <mark
              className={cn(
                "box-decoration-clone font-normal",
                dark
                  ? "bg-transparent text-accent"
                  : "rounded-[3px] bg-accent/20 px-0.5 text-background",
              )}
            >
              {parts.match}
            </mark>
          )}
          {parts.after}
        </p>
      </blockquote>

      {clampable && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={quoteId}
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 w-fit rounded-sm py-1 text-[14px] font-normal text-accent-ink underline underline-offset-4 transition-colors hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {expanded ? TESTIMONIALS_COPY.card.readLess : TESTIMONIALS_COPY.card.readMore}
        </button>
      )}

      <div className="mt-auto pt-6">
        {(context?.project || context?.caseStudy) && (
          <div className="mb-5 flex flex-col gap-1.5">
            {context?.project && (
              <p className={cn("text-[13px] leading-[1.4] lg:text-[14px]", dark ? "text-white/70" : "text-background/65")}>
                <span className={cn("font-normal", dark ? "text-white" : "text-background")}>
                  {TESTIMONIALS_COPY.card.projectLabel}:
                </span>{" "}
                {context.project}
              </p>
            )}
            {context?.caseStudy && (
              <Link
                href={context.caseStudy.href}
                data-analytics-cta="testimonials_case_study"
                data-analytics-location="testimonials"
                className={cn(
                  "group inline-flex w-fit items-center gap-1.5 rounded-sm text-[14px] font-normal underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  dark ? "text-accent" : "text-accent-ink",
                )}
              >
                {context.caseStudy.label}
                <ArrowIcon direction="right" className="size-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </div>
        )}

        <div className={cn("flex items-center gap-3 border-t pt-5", dark ? "border-white/15" : "border-background/10")}>
          {review.photo ? (
            <SanityImage
              image={review.photo}
              alt={`${review.clientName}, ${review.clientTitle}`}
              width={48}
              height={48}
              className="size-11 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className={cn(
                "flex size-11 shrink-0 items-center justify-center rounded-full text-[15px] font-normal",
                dark ? "bg-accent text-background" : "bg-background text-foreground",
              )}
            >
              {initialsOf(review.clientName)}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[16px] font-normal leading-[1.25]">{review.clientName}</p>
            <p className={cn("text-[13px] leading-[1.35] lg:text-[14px]", dark ? "text-white/70" : "text-background/65")}>
              {review.clientTitle}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

export function Testimonials({
  data,
  siteSettings,
  showPortfolioLink = true,
  showTrustBadges = true,
  showQuoteCta = true,
  onDarkBackdrop = false,
  composeFeed = true,
  leadReviewerName,
}: TestimonialsProps): React.JSX.Element {
  const reviews = useMemo(() => {
    const cmsReviews = data.reviews ?? [];
    const feed = composeFeed ? buildTestimonialFeed(cmsReviews) : cmsReviews;
    return rotateToReviewer(feed, leadReviewerName);
  }, [data.reviews, composeFeed, leadReviewerName]);
  const badges = siteSettings?.brandBadges;

  const trackRef = useRef<HTMLUListElement>(null);
  // Before hydration (and with JS off) assume the first three are in view;
  // the observer corrects it on mount.
  const [visible, setVisible] = useState<{ first: number; last: number }>({
    first: 0,
    last: Math.min(2, Math.max(reviews.length - 1, 0)),
  });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const items = Array.from(track.children) as HTMLElement[];
    const shown = new Set<number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const i = items.indexOf(entry.target as HTMLElement);
          if (i === -1) continue;
          if (entry.isIntersecting) shown.add(i);
          else shown.delete(i);
        }
        if (shown.size === 0) return;
        const sorted = [...shown].sort((a, b) => a - b);
        setVisible({ first: sorted[0]!, last: sorted[sorted.length - 1]! });
      },
      // A card counts as "in view" once most of it is on screen.
      { root: track, threshold: 0.6 },
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [reviews.length]);

  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    const target = track?.children[index] as HTMLElement | undefined;
    if (!track || !target) return;
    const padStart = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left: target.offsetLeft - padStart, behavior: reduce ? "auto" : "smooth" });
  }, []);

  const perPage = Math.max(1, visible.last - visible.first + 1);
  const atStart = visible.first <= 0;
  const atEnd = visible.last >= reviews.length - 1;
  const goPrev = useCallback(
    () => scrollToIndex(Math.max(0, visible.first - perPage)),
    [scrollToIndex, visible.first, perPage],
  );
  const goNext = useCallback(
    () => scrollToIndex(Math.min(reviews.length - 1, visible.last + 1)),
    [scrollToIndex, visible.last, reviews.length],
  );

  if (reviews.length === 0) return <></>;

  const platforms = (["clutch", "upwork"] as const)
    .filter((p) => reviews.some((r) => (r.source ?? "clutch") === p))
    .map((p) => TESTIMONIALS_COPY.platformNames[p])
    .join(" or ");

  const copy = TESTIMONIALS_COPY;
  const navButton =
    "flex size-12 items-center justify-center rounded-full border border-background/20 text-background transition-colors hover:border-background hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-foreground disabled:pointer-events-none disabled:opacity-35";

  const content = (
    <>
      {/* ---- Header: heading + aggregate ratings ---- */}
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
        <AnimatedSection className="max-w-[640px]">
          <Eyebrow tone="light" className="mb-4 lg:mb-5">
            {copy.eyebrow}
          </Eyebrow>
          <h2
            id="testimonials-heading"
            className="text-[36px] font-light leading-[1] tracking-[-0.03em] text-background sm:text-[44px] lg:text-[56px] xl:text-[64px]"
          >
            {data.testimonialsHeadingText}{" "}
            <span className="text-accent-ink">{data.testimonialsHeadingAccent}</span>
          </h2>
          <p className="mt-4 max-w-[520px] text-[16px] font-light leading-[1.5] text-background/75 lg:mt-5 lg:text-[18px]">
            {copy.intro(platforms)}
          </p>
        </AnimatedSection>

        {showTrustBadges && (
          <AnimatedSection className="grid grid-cols-2 gap-3 lg:w-[520px] lg:shrink-0 lg:gap-4">
            <a
              href={CLUTCH_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-analytics-cta="trust_badge_link"
              data-analytics-location="testimonials"
              data-analytics-label="Clutch"
              className="group flex flex-col gap-3 rounded-[18px] border border-background/[0.07] bg-white p-4 transition-shadow hover:shadow-[0_8px_28px_rgba(18,18,18,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:p-5"
            >
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                <SanityImage
                  image={badges?.clutchLogo}
                  fallbackSrc="/images/testimonials/clutch-logo.png"
                  alt=""
                  width={40}
                  height={40}
                  className="size-8 rounded-[6px] lg:size-9"
                />
                <span className="text-[28px] font-light leading-none tracking-[-0.02em] text-background lg:text-[32px]">
                  {copy.ratings.clutchScore}
                </span>
                <span className="flex basis-full sm:basis-auto">
                  <SanityImage
                    image={badges?.clutchStars}
                    fallbackSrc="/images/testimonials/stars.svg"
                    alt={copy.ratings.clutchStarsAlt}
                    width={95}
                    height={14}
                    className="h-2.5 w-auto lg:h-3"
                  />
                </span>
              </span>
              <span className="text-[13px] leading-[1.35] text-background/75 lg:text-[14px]">
                {CLUTCH_RATING_LABEL}
              </span>
              <span className="mt-auto inline-flex items-center gap-1 text-[13px] font-normal text-accent-ink underline underline-offset-4 lg:text-[14px]">
                {copy.ratings.clutchCta}
                <ArrowIcon direction="up-right" className="size-3.5" />
              </span>
              <NewTabHint />
            </a>

            <a
              href={UPWORK_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-analytics-cta="trust_badge_link"
              data-analytics-location="testimonials"
              data-analytics-label="Upwork"
              className="group flex flex-col gap-3 rounded-[18px] border border-background/[0.07] bg-white p-4 transition-shadow hover:shadow-[0_8px_28px_rgba(18,18,18,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:p-5"
            >
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                <SanityImage
                  image={badges?.upworkLogo}
                  fallbackSrc="/images/testimonials/upwork-logo.png"
                  alt=""
                  width={40}
                  height={40}
                  className="size-8 lg:size-9"
                />
                <span className="text-[28px] font-light leading-none tracking-[-0.02em] text-background lg:text-[32px]">
                  {UPWORK_RATING}
                </span>
                <span className="flex basis-full sm:basis-auto">
                  <SanityImage
                    image={badges?.upworkStars}
                    fallbackSrc="/images/testimonials/stars.svg"
                    alt={UPWORK_STARS_ALT}
                    width={95}
                    height={14}
                    className="h-2.5 w-auto lg:h-3"
                  />
                </span>
              </span>
              <span className="text-[13px] leading-[1.35] text-background/75 lg:text-[14px]">
                {UPWORK_RATING_LABEL}
              </span>
              <span className="mt-auto inline-flex items-center gap-1 text-[13px] font-normal text-accent-ink underline underline-offset-4 lg:text-[14px]">
                {copy.ratings.upworkCta}
                <ArrowIcon direction="up-right" className="size-3.5" />
              </span>
              <span className="sr-only">{`, ${UPWORK_BADGE_LABEL}`}</span>
              <NewTabHint />
            </a>
          </AnimatedSection>
        )}
      </div>

      {/* ---- Review row: scroll-snap, several cards in view ---- */}
      <ul
        ref={trackRef}
        className="scrollbar-hide relative -mx-5 mt-10 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto overscroll-x-contain px-5 pb-2 md:-mx-10 md:scroll-px-10 md:px-10 lg:mt-14 lg:gap-5"
      >
        {reviews.map((review, i) => (
          <li
            key={`${review.clientName}-${i}`}
            className={cn(
              "w-[80vw] max-w-[380px] shrink-0 snap-start sm:w-[360px]",
              i === 0 ? "lg:w-[520px] lg:max-w-none" : "lg:w-[380px]",
            )}
          >
            <ReviewCard review={review} featured={i === 0} index={i} total={reviews.length} />
          </li>
        ))}
      </ul>

      {/* ---- Progress + prev/next ---- */}
      {reviews.length > 1 && (
        <div role="group" aria-label={copy.nav.label} className="mt-6 flex items-center gap-4 lg:mt-8 lg:gap-6">
          <div aria-hidden="true" className="flex flex-1 gap-1.5">
            {reviews.map((review, i) => (
              <span
                key={`${review.clientName}-bar-${i}`}
                className={cn(
                  "h-1 flex-1 rounded-full transition-colors duration-300",
                  i >= visible.first && i <= visible.last ? "bg-background" : "bg-background/15",
                )}
              />
            ))}
          </div>
          <p className="shrink-0 text-[14px] tabular-nums text-background/70">
            {copy.nav.range(visible.first + 1, visible.last + 1, reviews.length)}
          </p>
          <div className="flex shrink-0 gap-2">
            <button type="button" aria-label={copy.nav.prev} onClick={goPrev} disabled={atStart} className={navButton}>
              <ArrowIcon direction="left" className="size-5" />
            </button>
            <button type="button" aria-label={copy.nav.next} onClick={goNext} disabled={atEnd} className={navButton}>
              <ArrowIcon direction="right" className="size-5" />
            </button>
          </div>
        </div>
      )}

      {/* ---- Next step ---- */}
      <div className="mt-10 flex flex-col gap-6 border-t border-background/10 pt-8 lg:mt-14 lg:flex-row lg:items-center lg:justify-between lg:pt-10">
        {showQuoteCta && (
          <div>
            <p className="text-[22px] font-light leading-[1.2] tracking-[-0.02em] text-background lg:text-[28px]">
              {copy.cta.heading}
            </p>
            <p className="mt-1.5 text-[15px] font-light leading-[1.5] text-background/70 lg:text-[16px]">
              {copy.cta.sub}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:gap-4">
          {showQuoteCta && (
            <Link
              href={copy.cta.quote.href}
              data-analytics-cta="testimonials_quote"
              data-analytics-location="testimonials"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-accent px-6 text-[16px] font-normal text-background transition-colors hover:bg-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background focus-visible:ring-offset-2 focus-visible:ring-offset-foreground lg:h-14 lg:px-8"
            >
              {copy.cta.quote.label}
              <ArrowIcon direction="right" className="size-5" />
            </Link>
          )}
          <a
            href={CLUTCH_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-cta="testimonials_all_reviews"
            data-analytics-location="testimonials"
            data-analytics-label="Clutch"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-background/25 px-6 text-[16px] font-normal text-background transition-colors hover:border-background hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-foreground lg:h-14 lg:px-8"
          >
            {copy.cta.allReviews.label}
            <ArrowIcon direction="up-right" className="size-4" />
            <NewTabHint />
          </a>
          {showPortfolioLink && (
            <Link
              href={copy.cta.portfolio.href}
              data-analytics-cta="testimonials_portfolio"
              data-analytics-location="testimonials"
              className="inline-flex h-12 items-center justify-center gap-1.5 rounded-sm px-2 text-[16px] font-normal text-background underline decoration-background/40 underline-offset-4 transition-colors hover:decoration-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {copy.cta.portfolio.label}
            </Link>
          )}
        </div>
      </div>
    </>
  );

  return (
    <section
      aria-labelledby="testimonials-heading"
      className="overflow-x-clip bg-foreground py-14 md:py-20 lg:py-24"
    >
      <Container>
        {onDarkBackdrop ? (
          <div className="rounded-[28px] bg-foreground/95 p-5 md:p-8 lg:p-10">{content}</div>
        ) : (
          content
        )}
      </Container>
    </section>
  );
}
